import { Head } from "@inertiajs/react";
import { ArrowRight, CheckCircle2, Code2 } from "lucide-react";

import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type GuideKey = "nextjs" | "rails" | "astro";

interface GuideStep {
  title: string;
  description: string;
}

interface GuideFaq {
  question: string;
  answer: string;
}

interface GuideConfig {
  badge: string;
  title: string;
  subtitle: string;
  stackLabel: string;
  codeTitle: string;
  codeLanguage: string;
  codeSnippet: string;
  steps: GuideStep[];
  faq: GuideFaq[];
}

const GUIDE_CONTENT: Record<GuideKey, GuideConfig> = {
  nextjs: {
    badge: "Next.js guide",
    title: "OG Image Generator for Next.js",
    subtitle: "Generate route-specific Open Graph images in Next.js and inject them into metadata from the server.",
    stackLabel: "Next.js App Router",
    codeTitle: "Server-side helper + metadata usage",
    codeLanguage: "typescript",
    codeSnippet: `// lib/og-image.ts\nimport { configure, createImage } from "og-pilot-js"\n\nconfigure((config) => {\n  config.apiKey = process.env.OG_PILOT_API_KEY\n  config.domain = process.env.OG_PILOT_DOMAIN\n})\n\nexport async function buildOgImage(post: {\n  title: string\n  description: string\n  path: string\n  imageUrl?: string\n}) {\n  return createImage(\n    {\n      template: "blog_post",\n      title: post.title,\n      description: post.description,\n      image_url: post.imageUrl,\n      path: post.path,\n    },\n    { iat: Math.floor(Date.now() / 1000) },\n  )\n}\n\n// app/blog/[slug]/page.tsx\nexport async function generateMetadata({ params }) {\n  const post = await getPost(params.slug)\n  const ogImage = await buildOgImage({\n    title: post.title,\n    description: post.excerpt,\n    path: "/blog/" + post.slug,\n  })\n\n  return {\n    openGraph: { images: [ogImage] },\n    twitter: { card: "summary_large_image", images: [ogImage] },\n  }\n}`,
    steps: [
      {
        title: "Install and configure the SDK",
        description: "Add og-pilot-js and configure API key + domain in server env variables.",
      },
      {
        title: "Create a server helper",
        description: "Build a reusable helper that signs payloads and returns OG image URLs.",
      },
      {
        title: "Generate page-specific metadata",
        description: "Call the helper in generateMetadata so each URL gets its own preview image.",
      },
      {
        title: "Publish and validate",
        description: "Deploy and validate with Open Graph debugger tools to refresh social caches.",
      },
    ],
    faq: [
      {
        question: "Should I call OG Pilot from client components?",
        answer: "No. Keep JWT signing and OG Pilot calls on the server only.",
      },
      {
        question: "Can each blog post have a unique image?",
        answer: "Yes. Pass per-post title, description, and path values when generating the image URL.",
      },
    ],
  },
  rails: {
    badge: "Rails guide",
    title: "OG Image Generator for Ruby on Rails",
    subtitle: "Use og_pilot_ruby in controllers/views to generate branded og:image URLs for each page request.",
    stackLabel: "Ruby on Rails",
    codeTitle: "Controller + meta tag integration",
    codeLanguage: "ruby",
    codeSnippet: `# Gemfile\ngem "og_pilot_ruby", "~> 0.4.1"\n\n# app/controllers/posts_controller.rb\nclass PostsController < ApplicationController\n  def show\n    @post = Post.find(params[:id])\n\n    @og_image = OgPilotRuby.create_image(\n      template: "blog_post",\n      title: @post.title,\n      description: @post.excerpt,\n      image_url: @post.cover_url,\n      path: request.fullpath,\n      iat: Time.current.to_i\n    )\n  end\nend\n\n# app/views/layouts/application.html.erb\n<meta property="og:image" content="<%= @og_image %>" />\n<meta name="twitter:card" content="summary_large_image" />\n<meta name="twitter:image" content="<%= @og_image %>" />`,
    steps: [
      {
        title: "Install og_pilot_ruby",
        description: "Add the gem, run bundle install, and configure credentials in environment variables.",
      },
      {
        title: "Generate URLs in controllers",
        description: "Call OgPilotRuby helpers with per-page data like title, description, and request path.",
      },
      {
        title: "Output meta tags in layout",
        description: "Set og:image and twitter:image to the generated OG Pilot URL.",
      },
      {
        title: "Verify live previews",
        description: "Use social debugger tools to confirm your new image is being fetched.",
      },
    ],
    faq: [
      {
        question: "Do I need a custom JWT service object?",
        answer: "No. Use og_pilot_ruby directly where you generate meta tags.",
      },
      {
        question: "Can I include request.fullpath for analytics context?",
        answer: "Yes. Include path in the payload to track and cache per-page image usage.",
      },
    ],
  },
  astro: {
    badge: "Astro guide",
    title: "OG Image Generator for Astro",
    subtitle: "Generate signed OG image URLs in Astro server context and render them in your layout metadata.",
    stackLabel: "Astro",
    codeTitle: "Astro server helper + layout metadata",
    codeLanguage: "typescript",
    codeSnippet: `// src/lib/og-image.ts\nimport { configure, createImage } from "og-pilot-js"\n\nconfigure((config) => {\n  config.apiKey = import.meta.env.OG_PILOT_API_KEY\n  config.domain = import.meta.env.OG_PILOT_DOMAIN\n})\n\nexport async function ogImageForPage(data: {\n  title: string\n  description: string\n  path: string\n}) {\n  return createImage(\n    {\n      template: "page",\n      title: data.title,\n      description: data.description,\n      path: data.path,\n    },\n    { iat: Math.floor(Date.now() / 1000) },\n  )\n}\n\n---\n// src/layouts/BaseLayout.astro\nimport { ogImageForPage } from "../lib/og-image"\n\nconst ogImage = await ogImageForPage({\n  title: Astro.props.title,\n  description: Astro.props.description,\n  path: Astro.url.pathname,\n})\n---\n<meta property="og:image" content={ogImage} />\n<meta name="twitter:card" content="summary_large_image" />\n<meta name="twitter:image" content={ogImage} />`,
    steps: [
      {
        title: "Configure OG Pilot credentials",
        description: "Set OG_PILOT_API_KEY and OG_PILOT_DOMAIN in Astro server env vars.",
      },
      {
        title: "Create a reusable helper",
        description: "Generate OG image URLs in server code using page-level data.",
      },
      {
        title: "Inject metadata in layout",
        description: "Set og:image and twitter:image tags in your Astro layout template.",
      },
      {
        title: "Re-scrape social URLs",
        description: "Use platform debuggers to refresh caches after publishing.",
      },
    ],
    faq: [
      {
        question: "Does this work for static Astro builds?",
        answer: "Yes. Generate URLs at build-time or in server-rendered routes depending on your setup.",
      },
      {
        question: "Can Astro pages use different OG Pilot templates?",
        answer: "Yes. Send template-specific payload fields for blog, product, event, and more.",
      },
    ],
  },
};

const DEFAULT_GUIDE: GuideKey = "nextjs";

interface FrameworkSocialImageProps {
  guide: string;
}

export default function FrameworkSocialImage({ guide }: FrameworkSocialImageProps) {
  const guideKey: GuideKey = guide in GUIDE_CONTENT ? (guide as GuideKey) : DEFAULT_GUIDE;
  const content = GUIDE_CONTENT[guideKey];

  return (
    <>
      <Head title={content.title} />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 pb-20 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="my-16 text-center">
            <Badge className="mb-3 bg-orange-100 tracking-[0.2em] text-orange-700 uppercase dark:bg-orange-900/30 dark:text-orange-300">
              {content.badge}
            </Badge>
            <h1 className="mb-4 text-4xl font-bold text-balance text-gray-900 md:text-5xl dark:text-white">
              {content.title}
            </h1>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              {content.subtitle}
            </p>
            {guideKey === "astro" ? (
              <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
                Comparing a static file, a build-time endpoint, and an on-demand API? Read{" "}
                <a href="/astro-og-image" className="font-medium text-orange-700 underline dark:text-orange-300">
                  Dynamic OG images for Astro
                </a>
                .
              </p>
            ) : null}
            {guideKey === "rails" ? (
              <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
                Choosing between a static file, drawing the card yourself into Active Storage, and caching an OG Pilot
                image on the record?{" "}
                <a href="/rails-og-image" className="font-medium text-orange-700 hover:underline dark:text-orange-300">
                  Read the Rails OG image comparison
                </a>
                .
              </p>
            ) : null}
          </section>

          <section className="mb-16 grid gap-6 md:grid-cols-4">
            {content.steps.map((step, index) => (
              <article
                key={step.title}
                className="rounded-2xl border border-orange-200/50 bg-white/95 p-6 shadow-lg dark:border-orange-500/30 dark:bg-gray-900/90"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white">
                  {index + 1}
                </span>
                <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{step.title}</h2>
                <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{step.description}</p>
              </article>
            ))}
          </section>

          <section className="mb-16 rounded-3xl bg-white p-8 shadow-xl dark:bg-gray-900/95 dark:shadow-none">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-sm font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
              <Code2 className="h-4 w-4" />
              <span>{content.stackLabel}</span>
            </div>
            <h2 className="mb-4 text-2xl font-semibold text-gray-900 dark:text-white">{content.codeTitle}</h2>
            <pre className="overflow-auto rounded-2xl border border-orange-200 bg-orange-50 p-6 text-sm leading-relaxed text-gray-900 dark:border-orange-900/40 dark:bg-gray-950 dark:text-gray-200">
              <code>{content.codeSnippet}</code>
            </pre>
          </section>

          <section className="mb-16 rounded-3xl border border-orange-200/60 bg-white/90 p-8 shadow-lg dark:border-orange-500/30 dark:bg-gray-900/90 dark:shadow-none">
            <h2 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-white">Common questions</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {content.faq.map((item) => (
                <article key={item.question}>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{item.question}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{item.answer}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="mb-20 rounded-3xl bg-gradient-to-r from-orange-500 via-orange-600 to-rose-500 p-10 text-white shadow-xl">
            <h2 className="mb-4 text-3xl font-bold">Ship branded previews for every URL</h2>
            <p className="mb-6 max-w-2xl text-sm leading-6 text-orange-100">
              OG Pilot turns page metadata into consistent, high-quality social previews without manual design exports.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-white text-orange-700 hover:bg-orange-50">
                <a href="/docs">
                  Read API docs
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/50 bg-white/10 text-white hover:bg-white/20"
              >
                <a href="/sign_up">
                  Start free trial
                  <CheckCircle2 className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </section>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
